import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Image, Upload } from "antd";

import { Checkbox } from "components/CheckRadiobox";
import { IcoInfo, TitleH2, TitleH3 } from "components/generic";
import { IInputHookOut, InputLabel } from "components/Input";
import { TMode } from "interfaces/intarfaces";
import { AboutTheRoomWrapper } from "./styles";
import { StyledBlock } from "components/containers";
import { ITextArea } from "components/TextArea";
import TextAreaWithSub from "components/TextAreaWithSub";
import { PRIVATE_USE, SHARED_USE } from "utils/constants";
import { InfoMessage } from "../RoomsDetails/styles";
import { RcFile, UploadFile } from "antd/lib/upload/interface";
import { EasyCrop } from "../../../components/MultiUpload";
import update from "immutability-helper";
import { DragableUploadListItem } from "../../../components/MultiUpload/DragableUploadListItem";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ALLOWED_UPLOAD_IMAGES_MIME_TYPES } from "../../../config/common";

interface IRoomsDetails {
  setFileTypeError: (error: string) => void;
  fileTypeError: string;
  modeView: TMode;
  bedroomSize: IInputHookOut;
  houseSize: IInputHookOut;
  listSharedUse: string[];
  listPrivateUse: string[];
  setListSharedUse: Function;
  setListPrivateUse: Function;
  descriptionAboutRoom: Partial<ITextArea>;
  currentPhotos: { url: string; order: number }[];
  setFilePictureList: Function;
  setFilesForDelete: Function;
  required?: boolean;
  filePictureList: File[];
  setIsPhotosEmpty: (isEmpty: any) => void;
  setReorderList: (order: { file_name: string; order: number }[]) => void;
  setOrderedNewImages: (
    order: { uid: string; name: string; order: number }[]
  ) => void;
}

export const AboutTheRoom: React.FC<IRoomsDetails> = ({
  fileTypeError,
  setFileTypeError,
  modeView,
  bedroomSize,
  houseSize,
  listSharedUse,
  listPrivateUse,
  setListSharedUse,
  setListPrivateUse,
  descriptionAboutRoom,
  currentPhotos,
  setFilePictureList,
  setFilesForDelete,
  setIsPhotosEmpty,
  filePictureList,
  required = false,
  setReorderList,
  setOrderedNewImages
}) => {
  const { t } = useTranslation("hostrooms");
  const [currentImage, setCurrentImage] = useState(undefined);
  const [visible, setVisible] = useState(false);
  const [fileListIn, setFileListIn] = useState<UploadFile<any>[]>([]);
  const [wrongImageFormat, setWrongImageFormat] = useState<boolean>(false);
  const [updateFlag, setUpdateFlag] = useState<boolean>(false);
  const [highPhotoResolutionList, setHighPhotoResolutionList] = useState<any>(
    {}
  );
  const [croppedImg, setCroppedImg] = useState<string>("");
  const [imgTitleToCrop, setImgTitleToCrop] = useState<string>("");
  const [croppedFile, setCroppedFile] = useState<any>();
  const [srcToCrop, setSrcToCrop] = useState<string>("");
  const [localUid, setLocalUid] = useState<string>("");

  const titleID = (keyItem: string) => {
    return {
      title: t(`aboutTheRoom.${keyItem}.title`),
      placeholder: t(`aboutTheRoom.${keyItem}.placeholder`),
      id: keyItem
    };
  };

  useEffect(() => {
    updateFlag && setIsPhotosEmpty(!fileListIn.length);
  }, [fileListIn, setIsPhotosEmpty]);

  const customRequest = ({ onSuccess, onError, file }: any) => {
    const checkInfo = () => {
      setTimeout(() => {
        setFilePictureList((prev: File[]) => [...prev, file]);
        onSuccess(null, file);
      }, 100);
    };

    checkInfo();
  };

  useEffect(() => {
    if (croppedFile) {
      if (croppedFile.name.includes("hospi_q_file")) {
        setFilePictureList((prev: File[]) => [
          //@ts-ignore
          ...prev.filter(({ uid, name }) =>
            uid ? uid !== localUid : name !== croppedFile.name
          ),
          croppedFile
        ]);
      } else {
        setFilePictureList((prev: File[]) => [
          ...prev.map((item: any) => {
            if (
              item.uid ? item.uid === localUid : item.name === croppedFile.name
            ) {
              return croppedFile;
            } else {
              return item;
            }
          })
        ]);
      }
      setCroppedFile(null);
    }
  }, [croppedFile]);

  const onChange = async ({ fileList: newFileList }: any) => {
    setFileTypeError("");
    if (wrongImageFormat) {
      return;
    }
    setUpdateFlag(true);
    setFileListIn(newFileList);
  };

  const onDelete = (file: UploadFile<any>) => {
    if (
      Object.keys(file).includes("url") &&
      file.name.includes("hospi_q_file")
    ) {
      setFilesForDelete((prev: string[]) => [
        ...prev,
        file.url?.split("/").slice(-1)[0]
      ]);
    } else {
      setFilePictureList((prev: RcFile[]) => [
        ...prev.filter((item) =>
          item.uid ? item.uid !== file.uid : item.name !== file.name
        )
      ]);
    }
  };

  useEffect(() => {
    if (currentPhotos.length) {
      const extImg = fileListIn
        .filter((item) => item.url)
        .map((item) => item.url);
      const files = currentPhotos

        .filter(({ url }) => !extImg.includes(url))
        .map((item: { url: string; order: number }, index: number) => ({
          uid: `${index}`,
          url: item.url,
          name: `hospi_q_file${index}`
        }));
      setFileListIn((prev) => [...prev, ...files]);
    }
    // eslint-disable-next-line
  }, [currentPhotos]);

  const onPreview = async (file: any) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    setCurrentImage(src);
    setVisible(true);
  };

  function readFile(file: any) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener("load", () => resolve(reader.result), false);
      reader.readAsDataURL(file);
    });
  }

  useEffect(() => {
    if (currentPhotos.length) {
      setHighPhotoResolutionList(
        currentPhotos.reduce((acc, item, index) => {
          acc[`hospi_q_file${index}`] = item.url;
          return acc;
        }, {} as any)
      );
    }
  }, [currentPhotos]);

  const onFileChange = async (e: any) => {
    let imageDataUrl: any = await readFile(e);
    setHighPhotoResolutionList((prev: any) => ({
      ...prev,
      [Object.keys(highPhotoResolutionList).includes(e.name) ? e.uid : e.name]:
        imageDataUrl
    }));
  };

  const selectedItem = (keyItem: "sharedUse" | "privateUse", item: string) => {
    return {
      title: t(`aboutTheRoom.${keyItem}.items.${item}.title`),
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.checked
          ? keyItem === "sharedUse"
            ? setListSharedUse((prev: string[]) => [...prev, item])
            : setListPrivateUse((prev: string[]) => [...prev, item])
          : keyItem === "sharedUse"
          ? setListSharedUse((prev: string[]) =>
              prev.filter((el) => el !== item)
            )
          : setListPrivateUse((prev: string[]) =>
              prev.filter((el) => el !== item)
            );
      },
      checked:
        keyItem === "sharedUse"
          ? listSharedUse.includes(item)
          : listPrivateUse.includes(item),
      p: "0"
    };
  };

  function beforeUpload(file: any) {
    onFileChange(file);
    const reader = new FileReader();

    reader.onload = (e) => {};
    const isJpgOrPng = ["image/jpeg", "image/png", "image/bmp"].includes(
      file.type
    );
    setWrongImageFormat(!isJpgOrPng);
    return isJpgOrPng;
  }

  useEffect(() => {
    if (imgTitleToCrop && croppedImg) {
      if (fileListIn.find(({ name, uid }) => name.includes(imgTitleToCrop))) {
        setFileListIn(
          fileListIn.map((item, index) =>
            item.uid === localUid && item.name === imgTitleToCrop
              ? {
                  uid: `-${index}${item.uid}`,
                  url: croppedImg,
                  name: imgTitleToCrop
                }
              : item
          )
        );
      } else {
        setFileListIn(
          fileListIn.map((item, index) =>
            item.uid === imgTitleToCrop
              ? {
                  uid: `-${index}${item.uid}`,
                  url: croppedImg,
                  name: imgTitleToCrop
                }
              : item
          )
        );
      }
      setSrcToCrop("");
      setImgTitleToCrop("");
      setCroppedImg("");
    }
  }, [croppedImg, imgTitleToCrop, localUid]);

  const handleSetImgToCrop = (key: string, uid: string, url?: string) => {
    const localKey = Object.keys(highPhotoResolutionList).includes(uid)
      ? uid
      : key;
    setLocalUid(uid);
    if (localKey && highPhotoResolutionList[localKey]) {
      setImgTitleToCrop(localKey);
      setSrcToCrop(highPhotoResolutionList[localKey]);
    }
  };

  const handleCanselCrop = () => {
    setSrcToCrop("");
    setImgTitleToCrop("");
    setCroppedImg("");
  };

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragRow = fileListIn[dragIndex];
      setFileListIn(
        update(fileListIn, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow]
          ]
        })
      );
    },
    [fileListIn]
  );

  const [isDragged, setIsDragged] = useState<boolean>(false);

  useEffect(() => {
    const focus = fileListIn.map(({ name }) => name);
    //@ts-ignore
    const array = [...filePictureList].sort(
      (a, b) => focus.indexOf(a.name) - focus.indexOf(b.name)
    );
    if (isDragged) {
      setFilePictureList(array.map((file) => file));
      setIsDragged(false);
    }
  }, [fileListIn, filePictureList]);

  function getKeyByValue(object: any, value: any) {
    return Object.keys(object).find((key) => object[key] === value);
  }

  useEffect(() => {
    if (currentPhotos.length) {
      const croppedIndxBE = filePictureList
        .filter(({ name }) => name.includes("hospi_q_file"))
        .map(({ name }) => +name.slice(-1) + 1);
      currentPhotos.forEach(({ order, url }) => {
        if (croppedIndxBE.includes(order)) {
          setFilesForDelete((prev: string[]) => [
            ...prev,
            url.split("/").slice(-1)[0]
          ]);
        }
      });
    }
    //Could be used in the future

    // const BECroppedNames = [...filePictureList].filter(({name}) => currentPhotos.filter(({url}) => Object.values(highPhotoResolutionList).includes(url)).map((valueUrl) => getKeyByValue(highPhotoResolutionList, valueUrl)).includes(name)).map(({name}) => name)
    // if(BECroppedNames.length) {
    //   fileListIn.forEach((item) => {
    //     if(BECroppedNames.includes(item.name)) {
    //       setFilesForDelete((prev: string[]) => [
    //         ...prev,
    //         currentPhotos[+item.name.slice(-1)].url.split("/").slice(-1)[0],
    //       ])
    //     }
    //   })
    // }
  }, [filePictureList, fileListIn, currentPhotos, highPhotoResolutionList]);

  const orderListWithoutNewPhotos = () => {
    setReorderList(
      fileListIn
        .filter(
          ({ url }) => url && currentPhotos.map(({ url }) => url).includes(url)
        )
        .map(({ url, uid }, index) => ({
          file_name: `downloads/${url!.split("/").slice(-1)[0]}`,
          order: fileListIn.findIndex(({ url: url_in }) => url_in === url) + 1
        }))
    );
  };

  useEffect(() => {
    if (currentPhotos.length) {
      orderListWithoutNewPhotos();
    }
  }, [fileListIn, currentPhotos, filePictureList]);

  useEffect(() => {
    setOrderedNewImages(
      //@ts-ignore FIXME: does UID exists on the File object?
      filePictureList.map(({ uid, name }) => ({
        uid: uid || "",
        name,
        order:
          fileListIn.findIndex(({ uid: uidIn, name: nameIn }) =>
            uid ? uid === uidIn : name === nameIn
          ) + 1
      }))
    );
  }, [fileListIn, filePictureList]);

  return (
    <AboutTheRoomWrapper error={required && !listSharedUse.length}>
      <TitleH2 id="titleAboutRoom" m="32px 0 0 0">
        {t("aboutTheRoom.title")}
      </TitleH2>
      <InputLabel
        {...bedroomSize}
        {...titleID("bedroomSize")}
        type="number"
        maxLength={5}
        onFocus={() =>
          Number(bedroomSize.value) === 0 && bedroomSize.onChange("")
        }
        disabled={modeView === "re-publish"}
      />
      <InputLabel
        {...houseSize}
        {...titleID("houseSize")}
        type="number"
        maxLength={5}
        onFocus={() => Number(houseSize.value) === 0 && houseSize.onChange("")}
        disabled={modeView === "re-publish"}
      />
      <StyledBlock id="sharedUse">
        <TitleH3>{t("aboutTheRoom.sharedUse.title")}</TitleH3>
        <ul>
          {SHARED_USE.map((item) => (
            <li key={item}>
              <Checkbox {...selectedItem("sharedUse", item)} />
            </li>
          ))}
        </ul>
      </StyledBlock>
      <StyledBlock id="privateUse">
        <TitleH3>{t("aboutTheRoom.privateUse.title")}</TitleH3>
        <ul>
          {PRIVATE_USE.map((item) =>
            item === "furnished room" ? (
              <li key={item}>
                <Checkbox {...selectedItem("privateUse", item)} p="0 8px 0 0" />
                <InfoMessage
                  left="-10em"
                  message={t(
                    "aboutTheRoom.privateUse.items.furnished room.tooltip"
                  )}
                >
                  <IcoInfo size="1.2em" />
                </InfoMessage>
              </li>
            ) : (
              <li key={item}>
                <Checkbox {...selectedItem("privateUse", item)} />
              </li>
            )
          )}
        </ul>
      </StyledBlock>
      <TextAreaWithSub
        {...descriptionAboutRoom}
        {...titleID("descriptionAboutRoom")}
        maxlenght={2000}
        subtitle={t("aboutTheRoom.descriptionAboutRoom.descriptiontip")}
      />
      {srcToCrop && (
        <EasyCrop
          src={srcToCrop}
          handleCanselCrop={handleCanselCrop}
          imgTitleToCrop={imgTitleToCrop}
          setCroppedImg={setCroppedImg}
          setCroppedFile={setCroppedFile}
        />
      )}
      <StyledBlock id="picturesRoom">
        <TitleH3>{t("aboutTheRoom.picturesRoom.title")}</TitleH3>
        <DndProvider backend={HTML5Backend}>
          <Upload
            accept={ALLOWED_UPLOAD_IMAGES_MIME_TYPES}
            beforeUpload={beforeUpload}
            customRequest={customRequest}
            itemRender={(originNode, file, currFileList) => (
              <DragableUploadListItem
                handleSetImgToCrop={handleSetImgToCrop}
                originNode={originNode}
                file={file}
                fileList={currFileList}
                moveRow={moveRow}
                setIsDragged={setIsDragged}
                disabled={modeView === "re-publish"}
              />
            )}
            multiple
            listType="picture-card"
            fileList={fileListIn}
            onChange={onChange}
            onPreview={onPreview}
            onRemove={onDelete}
            disabled={modeView === "re-publish"}
          >
            {fileListIn.length < 15 && "+ Upload"}
          </Upload>
        </DndProvider>
        <span style={{ color: "red" }}>{fileTypeError}</span>
        <div style={{ display: "none" }}>
          <Image.PreviewGroup
            preview={{ visible, onVisibleChange: (vis) => setVisible(vis) }}
          >
            <Image src={currentImage} />
          </Image.PreviewGroup>
        </div>
      </StyledBlock>
    </AboutTheRoomWrapper>
  );
};
